import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  createServiceCenterApi,
  editServiceCenterApi,
} from "../../services/ServiceApi";

const EditServiceCenterModal = ({ visible, onClose, onAfterSubmit, data }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address: "",
    contact_number: "",
    contact_email: "",
    google_map_link: "",
    google_review_link: "",
    total_cash: "0",
    total_baddebt: "0",
    total_unpaid_adjusted: "0",
    total_petty_cash: "0",
  });
  const [error, setError] = useState({});

  useEffect(() => {
    if (visible) {
      setForm({
        name: data?.name || "",
        address: data?.address || "",
        contact_number: data?.contact_number || "",
        contact_email: data?.contact_email || "-",
        google_map_link: data?.google_map_link || "",
        google_review_link: data?.google_review_link || "",
        total_cash: data?.total_cash || "0",
        total_baddebt: data?.total_baddebt || "0",
        total_unpaid_adjusted: data?.total_unpaid_adjusted || "0",
        total_petty_cash: data?.total_petty_cash || "0",
      });
    } else {
      handleReset();
    }
  }, [visible]);

  const handleReset = () => {
    setForm({
      name: "",
      address: "",
      contact_number: "",
      contact_email: "",
      google_map_link: "",
      google_review_link: "",
      total_cash: "0",
      total_baddebt: "0",
      total_unpaid_adjusted: "0",
      total_petty_cash: "0",
    });
    setError({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "contact_number") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setForm((prev) => ({ ...prev, [name]: numericValue }));

      if (numericValue.length === 10) {
        if (error?.contact_number) {
          setError((prev) => ({ ...prev, contact_number: "" }));
        }
      } else {
        setError((prev) => ({ ...prev, contact_number: "Must be 10 digits" }));
      }
    } else if (name === "contact_email") {
      setForm((prev) => ({ ...prev, [name]: value }));
      const emailRegex = /^\S+@\S+\.\S+$/;
      if (emailRegex.test(value)) {
        if (error?.contact_email) {
          setError((prev) => ({ ...prev, contact_email: "" }));
        }
      } else {
        setError((prev) => ({
          ...prev,
          contact_email: "Invalid email format",
        }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));

      // Clear error for other fields on input
      if (value && error?.[name]) {
        setError((prev) => ({ ...prev, [name]: "" }));
      }
    }
  };

  const handleSubmit = () => {
    let error = {};

    if (!form.name) error.name = "Name is required";
    if (!form.address) error.address = "Address is required";
    if (!form.contact_number)
      error.contact_number = "Contact number is required";
    else if (form.contact_number.length < 10)
      error.contact_number = "Must be at least 10 digits";
    if (!form.contact_email) error.contact_email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.contact_email))
      error.contact_email = "Invalid email format";
    if (!form.google_map_link) error.google_map_link = "This is required field";
    if (!form.google_review_link)
      error.google_review_link = "This is required field";

    setError(error);

    if (Object.keys(error).length === 0) {
      editServiceCenter();
    }
  };

  const editServiceCenter = () => {
    setLoading(true);
    editServiceCenterApi(data?.id || "", form)
      .then(() => {
        toast.success("Service Center Updated Successfully");
        handleReset();
        onAfterSubmit();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal show={visible} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Service Center</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="p-2">
          <div className="d-flex gap-2 mb-2">
            <div className="flex-fill">
              <label className="form-label text-dark">
                Service Center Name
              </label>
              <input
                type="text"
                name="name"
                className={`form-control ${error?.name ? "is-invalid" : ""}`}
                value={form.name}
                placeholder="DCW Service"
                onChange={handleInputChange}
              />
              {error?.name && (
                <small className="text-danger">{error.name}</small>
              )}
            </div>
            <div className="flex-fill">
              <label className="form-label text-dark">Contact Number</label>
              <input
                type="text"
                name="contact_number"
                className={`form-control ${
                  error?.contact_number ? "is-invalid" : ""
                }`}
                value={form.contact_number}
                placeholder="9876543210"
                onChange={handleInputChange}
              />
              {error?.contact_number && (
                <small className="text-danger">{error.contact_number}</small>
              )}
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label text-dark">Contact Email</label>
            <input
              type="text"
              name="contact_email"
              className={`form-control ${
                error?.contact_email ? "is-invalid" : ""
              }`}
              value={form.contact_email}
              placeholder="service@dcw.com"
              onChange={handleInputChange}
            />
            {error?.contact_email && (
              <small className="text-danger">{error.contact_email}</small>
            )}
          </div>
          <div className="mb-2">
            <label className="form-label text-dark">Address</label>
            <input
              type="text"
              name="address"
              className={`form-control ${error?.address ? "is-invalid" : ""}`}
              value={form.address}
              placeholder="123 Main Street"
              onChange={handleInputChange}
            />
            {error?.address && (
              <small className="text-danger">{error.address}</small>
            )}
          </div>

          <div className="d-flex gap-2 mb-2">
            <div className="flex-fill">
              <label className="form-label text-dark">Google Map Link</label>
              <input
                type="text"
                name="google_map_link"
                className={`form-control ${
                  error?.google_map_link ? "is-invalid" : ""
                }`}
                value={form.google_map_link}
                placeholder="e.g. https://maps.google.com/..."
                onChange={handleInputChange}
              />
              {error?.google_map_link && (
                <small className="text-danger">{error.google_map_link}</small>
              )}
            </div>
            <div className="flex-fill">
              <label className="form-label text-dark">Google Review Link</label>
              <input
                type="text"
                name="google_review_link"
                className={`form-control ${
                  error?.google_review_link ? "is-invalid" : ""
                }`}
                value={form.google_review_link}
                placeholder="e.g. https://maps.google.com/..."
                onChange={handleInputChange}
              />
              {error?.google_review_link && (
                <small className="text-danger">
                  {error.google_review_link}
                </small>
              )}
            </div>
          </div>

          <code className="text-light">
            <strong>Note:</strong> All fields are required. Please enter a valid
            email and a reachable contact number.
          </code>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end gap-2 w-100">
          <button
            className="btn btn-outline-dark"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading && <Spinner animation="border" size="sm" />} Update
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditServiceCenterModal;
